import React, { useState, useEffect } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    CircularProgress,
    TextField,
} from "@material-ui/core";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const specialtySchema = Yup.object().shape({
    name: Yup.string()
        .min(2, "Too Short!")
        .max(50, "Too Long!")
        .required("Required"),
});

const SpecialtyModal = ({ open, onClose, specialtyId, reload }) => {
    const [specialty, setSpecialty] = useState({
        name: "",
    });

    useEffect(() => {
        const fetchSpecialty = async () => {
            if (!specialtyId) return;
            try {
                const { data } = await api.get(`/specialties/${specialtyId}`);
                setSpecialty(data);
            } catch (err) {
                toastError(err);
            }
        };

        fetchSpecialty();
    }, [specialtyId, open]);

    const handleClose = () => {
        onClose();
        setSpecialty({ name: "" });
    };

    const handleSaveSpecialty = async (values) => {
        try {
            if (specialtyId) {
                await api.put(`/specialties/${specialtyId}`, values);
            } else {
                await api.post("/specialties", values);
            }
            toast.success(i18n.t("specialties.toasts.success"));
            reload();
            handleClose();
        } catch (err) {
            toastError(err);
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
            <DialogTitle>
                {specialtyId
                    ? i18n.t("specialties.modal.edit")
                    : i18n.t("specialties.modal.add")}
            </DialogTitle>
            <Formik
                initialValues={specialty}
                enableReinitialize={true}
                validationSchema={specialtySchema}
                onSubmit={(values, actions) => {
                    setTimeout(() => {
                        handleSaveSpecialty(values);
                        actions.setSubmitting(false);
                    }, 400);
                }}
            >
                {({ touched, errors, isSubmitting, isValid }) => (
                    <Form>
                        <DialogContent dividers>
                            <Field
                                as={TextField}
                                label={i18n.t("specialties.modal.form.name")}
                                autoFocus
                                name="name"
                                error={touched.name && Boolean(errors.name)}
                                helperText={touched.name && errors.name}
                                variant="outlined"
                                margin="dense"
                                fullWidth
                            />
                        </DialogContent>
                        <DialogActions>
                            <Button
                                onClick={handleClose}
                                color="secondary"
                                disabled={isSubmitting}
                                variant="outlined"
                            >
                                {i18n.t("global.buttons.cancel")}
                            </Button>
                            <Button
                                type="submit"
                                color="primary"
                                disabled={isSubmitting || !isValid}
                                variant="contained"
                                className={isSubmitting ? "circularProgress" : ""}
                            >
                                {i18n.t("global.buttons.add")}
                                {isSubmitting && (
                                    <CircularProgress
                                        size={24}
                                        className="buttonProgress"
                                    />
                                )}
                            </Button>
                        </DialogActions>
                    </Form>
                )}
            </Formik>
        </Dialog>
    );
};

export default SpecialtyModal;
